import { useEffect, useRef, type RefObject } from "react";

const RADIO = '[role="radio"]';
const GROUP = '[role="radiogroup"]';

// Arrow Down and Right move on, Up and Left back (DS-023, WAI-ARIA radio group)
const STEP: Record<string, number> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };

// The group's own radios, in page order: a nested group's belong to it
const radiosOf = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(RADIO)).filter(
    (radio) => radio.closest(GROUP) === root,
  );

const isEnabled = (radio: HTMLElement) =>
  !(radio as HTMLButtonElement).disabled && radio.getAttribute("aria-disabled") !== "true";

// One Tab stop among the radios: the checked enabled one, else the first
// enabled one. A checked disabled row stays checked and isn't the stop; with
// every row disabled there's none. Focus never moves here.
function syncTabStop(root: HTMLElement) {
  const radios = radiosOf(root);
  const enabled = radios.filter(isEnabled);
  const stop = enabled.find((radio) => radio.getAttribute("aria-checked") === "true") ?? enabled[0];
  for (const radio of radios) {
    const tabIndex = radio === stop ? 0 : -1;
    if (radio.tabIndex !== tabIndex) radio.tabIndex = tabIndex;
  }
}

/**
 * RadioGroup's keyboard model on web (molecules audit 2026-10-30, RadioGroup
 * #3): one Tab stop, and arrows that move to the next or previous enabled row
 * of this group, wrapping, and select it through the row's own press. It
 * works on the DOM under the root, so rows rendered inside other components
 * count. Only keys from this group's enabled radios are handled: a nested
 * group, or a control in a row's `children`, keeps its own.
 *
 * `onBlur` fires when focus leaves the group, not when it moves between rows
 * (RadioGroup #1). Native rows take no focus: `.native.ts` does nothing.
 */
export function useRadioGroupKeyboard(rootRef: RefObject<unknown>, onBlur?: () => void) {
  const onBlurRef = useRef(onBlur);
  useEffect(() => {
    onBlurRef.current = onBlur;
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!(root instanceof HTMLElement)) return;

    syncTabStop(root);
    // Selection, disabled rows and rows added anywhere under the root; not
    // tabindex, which syncTabStop writes
    const observer = new MutationObserver(() => syncTabStop(root));
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-checked", "disabled", "aria-disabled"],
    });

    const onKeyDown = (event: KeyboardEvent) => {
      const step = STEP[event.key];
      const { target } = event;
      if (step === undefined || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!(target instanceof HTMLElement) || !target.matches(RADIO)) return;
      if (target.closest(GROUP) !== root || !isEnabled(target)) return;
      const enabled = radiosOf(root).filter(isEnabled);
      const index = enabled.indexOf(target);
      if (index < 0) return;
      // No page scroll
      event.preventDefault();
      const next = enabled[(index + step + enabled.length) % enabled.length]!;
      next.focus();
      next.click();
    };

    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && root.contains(next)) return;
      onBlurRef.current?.();
    };

    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("focusout", onFocusOut);
    return () => {
      observer.disconnect();
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("focusout", onFocusOut);
    };
  }, [rootRef]);
}
